import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { getToken, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from the OAuth callback: AuthCallback establishes the session
    // first, so skip the /auth/me probe here to avoid a guaranteed 401.
    if (window.location.hash?.includes("session_token=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signIn = (token, nextUser) => {
    setToken(token);
    setUser(nextUser ?? null);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* the local session is cleared regardless of the server's answer */
    }
    clearToken();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, signIn, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
